// @ts-check
/**
 * Record an in-person sale: pick the event, pick pieces (product first, then size and color), take the
 * customer's details, choose how they pay (cash, UPI QR, or a Razorpay payment link), and say whether
 * the piece is handed over or shipped. Single-column with large tap targets, since it is used on a phone.
 */
import { Minus, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../context/ToastContext';
import adminPosOrderService from '../services/adminPosOrderService';
import adminSalesEventService from '../services/adminSalesEventService';
import { countryByIso } from '../utils/countries';
import { hasMeasurements } from '../utils/customerMeasurements';
import { defaultDispatchDate } from '../utils/dispatch';
import { autoSelect, colorOptions, hasStock, lineVariant, nextColorForSize, sizeOptions } from '../utils/posCatalog';
import {
  DISCOUNT_TYPES,
  ORDER_SCENARIOS,
  PAYMENT_METHODS,
  buildPosPayload,
  discountAmount,
  isDeepDiscount,
  listPrice,
  orderTotal,
  scenarioValue,
  validatePosForm,
} from '../utils/posOrder';
import { formatEventDates, pickDefaultEvent, todayInIndia } from '../utils/salesEvents';
import { STOREFRONT_URL } from '../utils/storefront';
import CountrySelect from './CountrySelect';
import MeasurementInputs from './MeasurementInputs';
import PosPaymentLinesInput from './PosPaymentLinesInput';
import PosProductPicker from './PosProductPicker';
import PosSaleResult from './PosSaleResult';
import WalkInLeadModal from './WalkInLeadModal';
import Modal from './ui/Modal';

const EVENT_KEY = 'kaay_pos_event_id';

const input = 'w-full px-3 py-3 text-base border border-input rounded-lg';
const label = 'block text-sm font-medium mb-1';
const inr = (amount) => `₹${Number(amount).toLocaleString('en-IN')}`;

// Remembered per browser, so the stand starts on the event it used last.
const rememberedEventId = () => {
  try {
    return localStorage.getItem(EVENT_KEY) || '';
  } catch {
    return '';
  }
};

const blankForm = (eventId) => ({
  eventId,
  firstName: '',
  lastName: '',
  phone: '',
  // ISO-2 of the country beside the phone number; empty = follow the selected event's country.
  phoneCountry: '',
  email: '',
  paymentMethod: 'cash',
  paymentReference: '',
  shipToCustomer: false,
  shipping_address_line1: '',
  shipping_address_line2: '',
  shipping_city: '',
  shipping_state: '',
  shipping_postal_code: '',
  shipping_country: 'India',
  soldAt: '',
  // Five working days out unless changed; only sent when the piece is being shipped.
  dispatchDate: defaultDispatchDate(todayInIndia()),
  notes: '',
  // A discount agreed on the spot, not a promo code - zero value means no discount.
  discountType: 'percentage',
  discountValue: 0,
  discountReason: '',
  paymentMode: 'full',
  paymentLines: [{ amount: 0, method: 'cash', reference: '' }],
});

/** A row of tap-to-choose buttons; the chosen one is filled in, unavailable ones are struck through. */
const OptionPills = ({ title, options, selected, isDisabled, onSelect }) => (
  <div>
    <p className="text-xs text-muted-foreground mb-1">{title}</p>
    <div className="flex flex-wrap gap-2">
      {options.map((option) => (
        <button
          key={option}
          onClick={() => onSelect(option)}
          disabled={isDisabled(option)}
          aria-pressed={selected === option}
          className={`min-w-12 px-3 py-2 text-base rounded-lg border disabled:opacity-40 disabled:line-through ${
            selected === option
              ? 'bg-primary text-primary-foreground border-foreground'
              : 'border-input hover:bg-muted/50'
          }`}
        >
          {option}
        </button>
      ))}
    </div>
  </div>
);

/**
 * One piece in the sale: which product, which size and color (always shown, always changeable),
 * how many, and at what price.
 */
const PieceLine = ({ line, onChange, onRemove }) => {
  const { product } = line;
  const sizes = sizeOptions(product);
  const colors = colorOptions(product, line.size);
  const variant = lineVariant(line);
  const missing = [sizes.length > 0 && !line.size && 'size', colors.length > 1 && !line.color && 'color'].filter(
    Boolean,
  );
  const deep = isDeepDiscount(line.unitPrice, listPrice(product));

  return (
    <div className="border border-border rounded-lg p-3 mb-2 space-y-3">
      <div className="flex justify-between gap-2">
        <div>
          <div className="text-sm font-medium">{product.name}</div>
          {variant ? (
            <div className="text-sm font-semibold text-green-700 dark:text-green-300">
              {[variant.size && `Size ${variant.size}`, variant.color, `${variant.stock_quantity} in stock`]
                .filter(Boolean)
                .join(' · ')}
            </div>
          ) : (
            <div className="text-sm font-semibold text-amber-700 dark:text-amber-300">
              Choose the {missing.join(' and ') || 'option'}
            </div>
          )}
        </div>
        <button
          onClick={onRemove}
          className="text-muted-foreground/70 hover:text-red-600 dark:hover:text-red-400 self-start"
          aria-label="Remove piece"
        >
          <Trash2 size={18} />
        </button>
      </div>

      {sizes.length > 1 && (
        <OptionPills
          title="Size"
          options={sizes}
          selected={line.size}
          isDisabled={(size) => !hasStock(product, { size })}
          onSelect={(size) => onChange({ size, color: nextColorForSize(product, size, line.color) })}
        />
      )}
      {colors.length > 1 && (
        <OptionPills
          title="Color"
          options={colors}
          selected={line.color}
          isDisabled={(color) => !hasStock(product, { size: line.size, color })}
          onSelect={(color) => onChange({ color })}
        />
      )}

      <div className="flex items-center gap-3">
        <div className="flex items-center border border-input rounded-lg">
          <button
            onClick={() => onChange({ quantity: Math.max(1, line.quantity - 1) })}
            className="p-3"
            aria-label="One fewer"
          >
            <Minus size={16} />
          </button>
          <span className="w-8 text-center text-base">{line.quantity}</span>
          <button onClick={() => onChange({ quantity: line.quantity + 1 })} className="p-3" aria-label="One more">
            <Plus size={16} />
          </button>
        </div>
        <label className="flex items-center gap-1 flex-1">
          <span className="text-base">{'₹'}</span>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            value={line.unitPrice}
            onChange={(e) => onChange({ unitPrice: Number(e.target.value) || 0 })}
            className={input}
            aria-label="Price per piece"
          />
        </label>
      </div>
      {variant && line.quantity > variant.stock_quantity && (
        <p className="text-xs text-red-600 dark:text-red-400">Only {variant.stock_quantity} in stock.</p>
      )}
      {deep && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          Half the list price ({inr(listPrice(product))}) or less. Check this is right.
        </p>
      )}
      <details className="text-sm">
        <summary className="cursor-pointer text-muted-foreground">
          Measurements{' '}
          {hasMeasurements(line) && (
            <span className="ml-1 px-2 py-0.5 rounded-md bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 text-xs">
              Measured
            </span>
          )}
        </summary>
        <div className="mt-2">
          <MeasurementInputs measurements={line.measurements} fitNotes={line.fitNotes} onChange={onChange} />
        </div>
      </details>
    </div>
  );
};

/**
 * @param {{onClose: () => void, requireEvent?: boolean, title?: string}} props requireEvent is false
 * for a general order not tied to any pop-up event (see CreateOrderModal)
 */
const PosOrderModal = ({ onClose, requireEvent = true, title = 'Record in-person sale' }) => {
  const toast = useToast();
  const [form, setFormRaw] = useState(() => blankForm(''));
  /** @type {[Array<{id: number, product: any, size: string | null, color: string | null, quantity: number, unitPrice: number, measurements: Record<string, string>, fitNotes: string}>, Function]} */
  const [lines, setLines] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [events, setEvents] = useState([]);
  const [loadState, setLoadState] = useState('loading');
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [result, setResult] = useState(null);
  const [showLead, setShowLead] = useState(false);
  const nextLineId = useRef(1);
  // One id per sale: a retried submit (bad Wi-Fi) returns the same order instead of a duplicate.
  const requestId = useRef(crypto.randomUUID());

  const setForm = (patch) => {
    setIsDirty(true);
    setFormRaw((prev) => ({ ...prev, ...patch }));
  };

  // The whole catalogue and the event list load once, so picking and filtering pieces needs no more requests.
  // A general order (requireEvent false) skips the event list entirely - it is never tied to one.
  const load = useCallback(async () => {
    setLoadState('loading');
    try {
      const [{ products }, activeEvents] = await Promise.all([
        adminPosOrderService.getCatalog(),
        requireEvent ? adminSalesEventService.list() : Promise.resolve([]),
      ]);
      setCatalog(products);
      setEvents(activeEvents);
      if (requireEvent) {
        setFormRaw((prev) =>
          prev.eventId
            ? prev
            : { ...prev, eventId: pickDefaultEvent(activeEvents, todayInIndia(), rememberedEventId()) },
        );
      }
      setLoadState('ready');
    } catch (err) {
      toast.error(err.message || 'Failed to load products and events');
      setLoadState('error');
    }
  }, [toast, requireEvent]);

  useEffect(() => {
    load();
  }, [load]);

  const addProduct = (product) => {
    setLines((prev) => [
      ...prev,
      {
        id: nextLineId.current++,
        product,
        ...autoSelect(product),
        quantity: 1,
        unitPrice: listPrice(product),
        measurements: {},
        fitNotes: '',
      },
    ]);
    setIsDirty(true);
  };

  const changeLine = (id, patch) => {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
    setIsDirty(true);
  };

  const removeLine = (id) => {
    setLines((prev) => prev.filter((l) => l.id !== id));
    setIsDirty(true);
  };

  const resolved = lines.map((l) => ({ ...l, variant: lineVariant(l) }));
  const subtotal = orderTotal(lines);
  const discount = discountAmount(subtotal, form.discountType, form.discountValue);
  const total = subtotal - discount;
  const isLink = form.paymentMode === 'full' && form.paymentMethod === 'razorpay_link';

  // The phone country follows the selected event until staff pick one; India when there is no event.
  const eventCountry = events.find((e) => e.id === form.eventId)?.country_code;
  const phoneCountry = form.phoneCountry || countryByIso(eventCountry)?.iso || 'IN';

  const handleClose = () => {
    if (isDirty && !result && !window.confirm('This sale is not saved yet. Discard it?')) return;
    onClose();
  };

  const handleSubmit = async () => {
    const sale = { ...form, phoneCountry };
    const problem = validatePosForm(sale, resolved, { requireEvent });
    if (problem) {
      toast.error(problem);
      return;
    }
    const deep = resolved.filter((l) => isDeepDiscount(l.unitPrice, listPrice(l.product)));
    if (
      deep.length > 0 &&
      !window.confirm(
        `${deep.map((l) => `${l.product.name} at ${inr(l.unitPrice)}`).join(', ')}: half the list price or less. Continue?`,
      )
    ) {
      return;
    }

    setSaving(true);
    try {
      const response = await adminPosOrderService.createPosOrder(
        buildPosPayload({ form: sale, lines: resolved, requestId: requestId.current }),
      );
      if (form.eventId) {
        try {
          localStorage.setItem(EVENT_KEY, form.eventId);
        } catch {
          // Remembering the event is a convenience only.
        }
      }
      setIsDirty(false);
      setResult({ response, form: sale, lines: resolved });
    } catch (err) {
      toast.error(
        `${err.message || 'Failed to record the sale'}. Tapping the button again is safe: it will not create a duplicate.`,
      );
    } finally {
      setSaving(false);
    }
  };

  const startAnother = () => {
    requestId.current = crypto.randomUUID();
    setFormRaw(blankForm(form.eventId));
    setLines([]);
    setIsDirty(false);
    setResult(null);
  };

  if (result) {
    return (
      <Modal maxWidth="lg" onClose={onClose} hasUnsavedChanges={false}>
        <PosSaleResult {...result} onDone={onClose} onAnother={startAnother} />
      </Modal>
    );
  }

  return (
    <Modal maxWidth="lg" onClose={handleClose} hasUnsavedChanges={isDirty}>
      <div className="px-6 py-4 border-b flex items-center justify-between">
        <h2 className="text-lg font-semibold">{title}</h2>
        {form.eventId && (
          <button
            onClick={() => setShowLead(true)}
            className="text-sm px-3 py-1.5 border border-input rounded-lg hover:bg-muted/50"
          >
            Capture a lead
          </button>
        )}
      </div>

      <div className="px-6 py-4 space-y-5 max-h-[65vh] overflow-y-auto">
        {loadState === 'loading' && <p className="text-sm text-muted-foreground">Loading products and events...</p>}
        {loadState === 'error' && (
          <div className="rounded-lg bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-900 px-4 py-3 text-sm text-red-800 dark:text-red-300">
            Could not load products and events.{' '}
            <button onClick={load} className="underline font-medium">
              Try again
            </button>
          </div>
        )}

        {requireEvent && (
          <div>
            <label className={label}>Event *</label>
            <select className={input} value={form.eventId} onChange={(e) => setForm({ eventId: e.target.value })}>
              <option value="">Choose the event...</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name} ({formatEventDates(event)})
                </option>
              ))}
            </select>
            {loadState === 'ready' && events.length === 0 && (
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                No active events yet.{' '}
                <a href="/sales-events" target="_blank" rel="noopener noreferrer" className="underline">
                  Add one on the Sales Events page
                </a>{' '}
                (opens in a new tab, so this sale is kept), then reopen this form.
              </p>
            )}
          </div>
        )}

        <div>
          <label className={label}>Pieces *</label>
          {resolved.map((line) => (
            <PieceLine
              key={line.id}
              line={line}
              onChange={(patch) => changeLine(line.id, patch)}
              onRemove={() => removeLine(line.id)}
            />
          ))}
          <PosProductPicker products={catalog} onPick={addProduct} />
        </div>

        {lines.length > 0 && (
          <div className="border border-border rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className={`${label} mb-0`}>Discount agreed at the stand</span>
              <div className="flex gap-1 ml-auto">
                {DISCOUNT_TYPES.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setForm({ discountType: t.value })}
                    className={`w-9 py-1 text-sm rounded-lg border ${
                      form.discountType === t.value
                        ? 'bg-primary text-primary-foreground border-foreground'
                        : 'border-input hover:bg-muted/50'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <input
                type="number"
                inputMode="decimal"
                min={0}
                value={form.discountValue}
                onChange={(e) => setForm({ discountValue: Number(e.target.value) || 0 })}
                className={`${input} w-24`}
                aria-label="Discount value"
              />
            </div>
            {discount > 0 && (
              <>
                <div className="text-sm text-muted-foreground flex justify-between">
                  <span>
                    Subtotal {inr(subtotal)} &minus; discount {inr(discount)}
                  </span>
                  <span className="font-medium text-foreground">Total {inr(total)}</span>
                </div>
                <input
                  className={input}
                  placeholder="Discount reason, e.g. Soho House Popup Spl Discount"
                  value={form.discountReason}
                  onChange={(e) => setForm({ discountReason: e.target.value })}
                  aria-label="Discount reason"
                />
              </>
            )}
          </div>
        )}

        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              className={input}
              placeholder="First name *"
              value={form.firstName}
              onChange={(e) => setForm({ firstName: e.target.value })}
            />
            <input
              className={input}
              placeholder="Last name"
              value={form.lastName}
              onChange={(e) => setForm({ lastName: e.target.value })}
            />
          </div>
          <div className="flex gap-2">
            <CountrySelect
              variant="phone"
              ariaLabel="Country code"
              value={phoneCountry}
              onChange={(iso) => setForm({ phoneCountry: iso })}
            />
            <input
              className={`${input} min-w-0`}
              type="tel"
              inputMode="tel"
              placeholder="WhatsApp number *"
              value={form.phone}
              onChange={(e) => setForm({ phone: e.target.value })}
            />
          </div>
          <input
            className={input}
            type="email"
            placeholder="Email (optional)"
            value={form.email}
            onChange={(e) => setForm({ email: e.target.value })}
          />
        </div>

        <div>
          <label className={label}>What&apos;s happening with this sale? *</label>
          <select
            className={input}
            value={scenarioValue(form.shipToCustomer, form.paymentMode)}
            onChange={(e) => {
              const scenario = ORDER_SCENARIOS.find((s) => s.value === e.target.value);
              if (scenario) setForm({ shipToCustomer: scenario.shipToCustomer, paymentMode: scenario.paymentMode });
            }}
          >
            {ORDER_SCENARIOS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>

          <div className="mt-3">
            {form.paymentMode === 'partial' ? (
              <PosPaymentLinesInput lines={form.paymentLines} onChange={(paymentLines) => setForm({ paymentLines })} />
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {PAYMENT_METHODS.map((m) => {
                    const unavailable = m.value === 'razorpay_link' && !STOREFRONT_URL;
                    return (
                      <button
                        key={m.value}
                        onClick={() =>
                          setForm({ paymentMethod: m.value, soldAt: m.value === 'razorpay_link' ? '' : form.soldAt })
                        }
                        disabled={unavailable}
                        title={unavailable ? 'REACT_APP_STOREFRONT_URL is not set' : undefined}
                        className={`px-2 py-3 text-base rounded-lg border disabled:opacity-40 ${
                          form.paymentMethod === m.value
                            ? 'bg-primary text-primary-foreground border-foreground'
                            : 'border-input hover:bg-muted/50'
                        }`}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>
                {form.paymentMethod === 'cash' && (
                  <p className="text-xs text-muted-foreground mt-2">Tap Record once you have the cash in hand.</p>
                )}
                {form.paymentMethod === 'upi_qr' && (
                  <div className="mt-2">
                    <p className="text-xs text-muted-foreground mb-1">
                      Record this only after the credit shows in your UPI app.
                    </p>
                    <input
                      className={input}
                      placeholder="UPI reference (optional)"
                      value={form.paymentReference}
                      onChange={(e) => setForm({ paymentReference: e.target.value })}
                    />
                  </div>
                )}
                {isLink && (
                  <p className="text-xs text-muted-foreground mt-2">
                    You will get a link and a QR to share once this is saved.
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        <div>
          {form.shipToCustomer && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <input
                className={`${input} col-span-2`}
                placeholder="Address line 1 *"
                value={form.shipping_address_line1}
                onChange={(e) => setForm({ shipping_address_line1: e.target.value })}
              />
              <input
                className={`${input} col-span-2`}
                placeholder="Address line 2"
                value={form.shipping_address_line2}
                onChange={(e) => setForm({ shipping_address_line2: e.target.value })}
              />
              <input
                className={input}
                placeholder="City *"
                value={form.shipping_city}
                onChange={(e) => setForm({ shipping_city: e.target.value })}
              />
              <input
                className={input}
                placeholder="State *"
                value={form.shipping_state}
                onChange={(e) => setForm({ shipping_state: e.target.value })}
              />
              <input
                className={input}
                inputMode="numeric"
                placeholder="Postal code *"
                value={form.shipping_postal_code}
                onChange={(e) => setForm({ shipping_postal_code: e.target.value })}
              />
              <input
                className={input}
                placeholder="Country"
                value={form.shipping_country}
                onChange={(e) => setForm({ shipping_country: e.target.value })}
              />
              <label className="col-span-2 text-sm">
                Promised dispatch date
                <input
                  type="date"
                  className={`${input} mt-1`}
                  value={form.dispatchDate}
                  min={todayInIndia()}
                  onChange={(e) => setForm({ dispatchDate: e.target.value })}
                />
                <span className="text-xs text-muted-foreground">
                  Five working days from today unless you change it.
                </span>
              </label>
            </div>
          )}
        </div>

        <textarea
          className={input}
          rows={2}
          placeholder="Notes (e.g. paper bill number)"
          value={form.notes}
          onChange={(e) => setForm({ notes: e.target.value })}
        />

        {!isLink && (
          <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground">Sold earlier and entering it now?</summary>
            <input
              type="datetime-local"
              className={`${input} mt-2`}
              value={form.soldAt}
              onChange={(e) => setForm({ soldAt: e.target.value })}
            />
            <p className="text-xs text-muted-foreground mt-1">Leave empty for a sale happening now.</p>
          </details>
        )}
      </div>

      {showLead && (
        <WalkInLeadModal salesEventId={form.eventId} eventCountry={eventCountry} onClose={() => setShowLead(false)} />
      )}

      <div className="flex items-center gap-3 px-6 py-4 border-t">
        <button
          onClick={handleSubmit}
          disabled={saving || loadState !== 'ready'}
          className="flex-1 px-4 py-3 text-base bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:bg-muted-foreground/50"
        >
          {saving
            ? 'Saving...'
            : isLink
              ? `Create payment link · ${inr(total)}`
              : form.paymentMode === 'partial'
                ? `Record advance · ${inr(form.paymentLines.reduce((sum, l) => sum + l.amount, 0))} of ${inr(total)}`
                : `Record sale · ${inr(total)}`}
        </button>
        <button
          onClick={handleClose}
          disabled={saving}
          className="px-4 py-3 text-base border border-input rounded-lg hover:bg-accent"
        >
          Cancel
        </button>
      </div>
    </Modal>
  );
};

export default PosOrderModal;
